import { Add, SubtractAlt } from '@carbon/react/icons';
import searchAtomPair, { type SearchParams } from 'atoms/search-atom';
import {
  Button,
  ComboBox,
  InlineNotification,
  Modal,
  Layer,
} from '@carbon/react';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { DestroyRecordButton } from 'components/destroy-record-button';
import { FormRow, FormSection } from 'components/forms';
import { Link } from 'components/link';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { debounce } from 'lodash-es';
import { useMemo, useReducer, useState } from 'react';
import { AliasedEntityQueryResult, EntityResponse } from 'types/api-responses';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';

import style from './aliases.module.scss';
import { aliasCreatedAtom, createAliasAtom, deleteAliasAtom } from './atoms';
import { useServerDrivenTable } from './use-server-driven-table';
import {
  FlagOverrideModal,
  isFlagOverrideRequired,
  FLAG_OVERRIDE_MODAL_SELECTOR,
  type FlaggedEntitySummary,
} from 'components/flag-override-modal';

type AliasRow = AliasedEntityQueryResult & Record<string, unknown>;

const DEFAULT_PARAMS: SearchParams = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  sortAttribute: 'name',
  sortDirection: 'ASC',
};

// Sortable column ids map to Query::Entities::SORTABLE_ATTRIBUTES. Type
// (canonical) isn't an Elasticsearch sort field, so it stays non-sortable.
const buildColumns = (
  entity: EntityResponse,
  reload: () => void
): DataTableColumnDef<AliasRow>[] => [
  {
    id: 'id',
    accessorFn: (row) => row.entity.id,
    header: 'ID',
    size: 160,
    cell: ({ row }) => (
      <>
        {row.original.entity.id}{' '}
        {row.original.entity.id === entity.id && '(current entity)'}
      </>
    ),
  },
  {
    id: 'name',
    accessorFn: (row) => row.entity.name,
    header: 'Name',
    size: 300,
  },
  {
    id: 'type',
    accessorFn: (row) => (row.canonical ? 'Primary' : 'Alias'),
    header: 'Type',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'actions',
    header: 'Action',
    size: 160,
    enableSorting: false,
    // The delete button's icon tooltip needs to escape the cell; the default
    // truncating cell clips it.
    meta: { overflowVisible: true },
    cell: ({ row }) => {
      const el = row.original;
      if (el.entity.id === entity.id) return null;
      const aliasHref = el.resources.alias?.href;
      return (
        <div className={style.deleteAliasRow}>
          <Link to={el.entity.href}>Details</Link>
          {aliasHref && (
            // DestroyRecordButton portals its confirmation modal to <body>, so
            // it escapes the DataTable's `contain: layout` body — no need to
            // hoist the modal out of the row.
            <DestroyRecordButton
              deleteAtom={deleteAliasAtom}
              url={aliasHref}
              onSuccess={reload}
              hasIconOnly
              renderIcon={SubtractAlt}
              iconDescription="Remove"
              tooltipPosition="left"
            />
          )}
        </div>
      );
    },
  },
];

const Aliases = ({ entity }: { entity: EntityResponse }) => {
  const [openModal, toggleOpenModal] = useReducer((s) => !s, false);
  const aliasCreated = useAtomValue(aliasCreatedAtom);
  const resetAliasCreated = useResetAtom(aliasCreatedAtom);

  const { tableProps, reload } = useServerDrivenTable<AliasRow>({
    href: entity.resources.queries.aliasedEntities.href,
    defaultParams: DEFAULT_PARAMS,
  });

  const columns = useMemo(() => buildColumns(entity, reload), [entity, reload]);

  const addButton = (
    <Button
      key="add-alias"
      size="sm"
      renderIcon={Add}
      onClick={() => {
        toggleOpenModal();
        resetAliasCreated();
      }}
    >
      Add Alias
    </Button>
  );

  return (
    <>
      {aliasCreated && (
        <InlineNotification lowContrast kind="success" title="Alias created!" />
      )}
      <DataTable<AliasRow>
        {...tableProps}
        columns={columns}
        getRowId={(row) => String(row.entity.id)}
        initialTableState={{ sortColumn: 'name', sortDirection: 'asc' }}
        searchPlaceholder="Search aliases"
        rightToolbarActions={[addButton]}
        config={{ columnFilters: false, columnReordering: false }}
        persistenceKey={`entity-${entity.id}-aliases-table`}
        maxBodyHeight="60vh"
      />
      <AddAliasModal
        open={openModal}
        onClose={() => toggleOpenModal()}
        entity={entity}
        getAliases={reload}
      />
    </>
  );
};

const AddAliasModal = ({
  onClose,
  open,
  entity,
  getAliases,
}: {
  onClose: () => void;
  open: boolean;
  entity: EntityResponse;
  getAliases: () => void;
}) => {
  const [error, setError] = useState<string | undefined>();
  const { searchAtom, resetSearchDataAtom } = useMemo(
    () => searchAtomPair<EntityResponse>('entities'),
    []
  );
  const resetSearchData = useSetAtom(resetSearchDataAtom);
  const entityData = entity;
  const createAlias = useSetAtom(createAliasAtom);
  const createAliasState = useAtomValue(createAliasAtom);
  const [selectedEntity, setSelectedEntity] = useState<
    EntityResponse | undefined
  >();
  const setAliasCreated = useSetAtom(aliasCreatedAtom);
  const [searchResults, searchPayload] = useAtom(searchAtom);
  const [flaggedForOverride, setFlaggedForOverride] = useState<
    FlaggedEntitySummary[] | null
  >(null);
  const debouncedSearchPayload = useMemo(
    () => debounce(searchPayload, 400),
    [searchPayload]
  );

  // Either end of the alias being flagged returns a 422 asking for an override
  // rationale; passing one re-submits and proceeds (PROD/2907373590).
  const submitAlias = (
    overrideRationale?: string,
    falsePositiveIds?: number[]
  ) => {
    if (!entityData || !selectedEntity) return;
    createAlias({
      url: entityData.resources.fromAliases.href,
      entityId: selectedEntity.id,
      overrideRationale,
      falsePositiveIds,
      onSuccess: () => {
        setFlaggedForOverride(null);
        getAliases();
        setSelectedEntity(undefined);
        setAliasCreated(true);
        onClose();
      },
      onError: (response) => {
        if (isFlagOverrideRequired(response)) {
          setFlaggedForOverride(response.flaggedEntities);
          return;
        }
        if (!response) return;
        setError(response.errors.to);
      },
    });
  };

  return (
    <Modal
      modalHeading="Add Alias"
      primaryButtonText={createAliasState.loading ? 'Saving...' : 'Save'}
      primaryButtonDisabled={createAliasState.loading}
      open={open}
      onRequestSubmit={() => submitAlias()}
      secondaryButtonText="Cancel"
      onRequestClose={() => {
        onClose();
        setSelectedEntity(undefined);
      }}
      className={style.allowOverflow}
      // Let the consolidation-risk override modal (portaled to <body>) hold focus.
      selectorsFloatingMenus={[FLAG_OVERRIDE_MODAL_SELECTOR]}
    >
      <FlagOverrideModal
        open={!!flaggedForOverride}
        flaggedEntities={flaggedForOverride ?? []}
        loading={createAliasState.loading}
        onCancel={() => setFlaggedForOverride(null)}
        onConfirm={(rationale, falsePositiveIds) =>
          submitAlias(rationale, falsePositiveIds)
        }
      />
      <span>
        The alias with the lowest ID will be displayed as the Primary entity in
        title chain calculation outputs.
      </span>
      <FormSection>
        <FormRow>
          <Layer>
            <ComboBox
              id="aliased-entity"
              titleText={'Alias'}
              items={searchResults?.data?.results || []}
              placeholder=""
              itemToString={(data) => {
                return data ? `${data.name}, ${data.id}` : '';
              }}
              selectedItem={selectedEntity || null}
              onChange={({ selectedItem }) => {
                if (selectedItem) setSelectedEntity(selectedItem);
              }}
              onInputChange={(text) => {
                setError(undefined);
                resetSearchData(undefined);
                debouncedSearchPayload((current) => ({
                  ...current,
                  text,
                  pageSize: 10,
                }));
              }}
              invalid={!!error}
              invalidText={error}
            />
          </Layer>
        </FormRow>
      </FormSection>
    </Modal>
  );
};

export { Aliases };
